/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {visiblePageNumbers} from '../directives/pagination/visible-page-numbers';

export interface AdminListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

export abstract class AdminResourceListPageBase implements OnInit, OnChanges {
  @Input() list: any;

  hideHidden = false;

  protected constructor(
    @Inject(LocalNetworkProfile) protected LocalNetworkProfile: any
  ) {
  }

  ngOnInit(): void {
    this.hideHidden = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.afterListInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.list) {
      this.afterListInit();
    }
  }

  /**
   * Line-item list pattern: subclasses wire children, bulk actions, and other
   * list extras here. Called on init and whenever the list input changes.
   */
  protected afterListInit(): void {
  }

  abstract readonly itemStateName: string;

  /** Sort header params from column selector state, matching line-item list pages. */
  get sortHeaderParams(): any[] {
    if (this.list?.sel?.outputFields?.length) {
      return this.list.sel.outputFields;
    }
    return this.list?.defaultFields || [];
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.list.currentPage) {
      return;
    }
    this.list.currentPage = page;
    this.list.changePage();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.list.totalCount / this.list.pageSize));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.list.currentPage, this.totalPages());
  }

  pageRangeStart(): number {
    return this.list.pageSize * (this.list.currentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.list.pageSize * this.list.currentPage, this.list.totalCount);
  }

  columnLabel(column: AdminListColumn): string {
    if (column.labelInstant) {
      return column.labelInstant;
    }
    const key = column.label || column.param;
    return key ? 'label.' + key : '';
  }

  isColumnSortable(column: AdminListColumn): boolean {
    return column.sortable !== false && !!column.param;
  }

  sortBy(column: AdminListColumn): void {
    if (column.numeric) {
      this.list.makeSortQuery(column.param, column.numeric);
    } else {
      this.list.makeSortQuery(column.param);
    }
  }

  changeIncludeFilter(value: string): void {
    if (this.list.showingInclude === value) {
      return;
    }
    this.list.makeIncludeQuery();
  }

  protected normalizeColumns(fields: any[]): AdminListColumn[] {
    return _.map(fields, (f: any) => {
      if (_.isString(f)) {
        return {param: f};
      }
      return {
        param: f.param ?? f.label ?? '',
        label: f.label,
        labelInstant: f.labelInstant,
        numeric: f.numeric,
        sortable: f.sortable
      };
    });
  }

  protected listFieldId(field: any): string {
    if (_.isString(field)) {
      return field;
    }
    return field.param || field.label || field;
  }

  protected isListFieldVisible(param: string, defaultFields: any[] = this.list?.defaultFields || []): boolean {
    const selFields = this.list?.sel?.selFields;
    if (!selFields?.length) {
      return _.some(defaultFields, (f: any) => this.listFieldId(f) === param);
    }
    return selFields.indexOf(param) > -1;
  }
}
